import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listProvincias } from "@/lib/anuncios.functions";
import { AGENCIA_ESTADOS, AGENCIA_ORIGENES, AGENCIA_SEGUIMIENTOS } from "@/lib/agencias.functions";
import { cn } from "@/lib/utils";

import { NumInput, MultiCheck } from "./AnunciosFiltersForm";

export type AgenciasFilters = {
  nombre: string;
  provincia_ids: string[];
  estado: string[];
  seguimiento: string[];
  anuncios_min: number | null;
  anuncios_max: number | null;
  origen: string[];
  /** true = con miembros, false = sin miembros, null = indiferente. */
  con_miembros: boolean | null;
  con_solicitudes: boolean | null;
  /** Con invitaciones pendientes de aceptar. */
  con_invitaciones: boolean | null;
  /** Con teléfono o email de contacto. */
  con_contacto: boolean | null;
  /** Alta en los últimos N días (null = cualquier fecha). */
  alta_dias: number | null;
};

export const emptyAgenciasFilters: AgenciasFilters = {
  nombre: "",
  provincia_ids: [],
  estado: [],
  seguimiento: [],
  anuncios_min: null,
  anuncios_max: null,
  origen: [],
  con_miembros: null,
  con_solicitudes: null,
  con_invitaciones: null,
  con_contacto: null,
  alta_dias: null,
};

export function countActiveAgenciasFilters(f: AgenciasFilters): number {
  let n = 0;
  for (const v of Object.values(f)) {
    if (Array.isArray(v)) {
      if (v.length) n += 1;
    } else if (typeof v === "string") {
      if (v.trim()) n += 1;
    } else if (v != null) {
      n += 1;
    }
  }
  return n;
}

export function normalizeText(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

const ESTADO_LABEL: Record<string, string> = {
  activa: "Activa",
  inactiva: "Inactiva",
  bloqueada: "Bloqueada",
};

export const AGENCIA_ESTADO_LABEL = ESTADO_LABEL;

export const AGENCIA_ORIGEN_LABEL: Record<string, string> = {
  registro: "Registro",
  lead_web: "Lead web",
  manual: "Manual",
  import: "Importación",
  captado: "Captado",
};

export const AGENCIA_ALTA_OPTIONS = [
  { value: 7, label: "Últimos 7 días" },
  { value: 30, label: "Últimos 30 días" },
  { value: 90, label: "Últimos 90 días" },
  { value: 365, label: "Último año" },
] as const;

export const SEGUIMIENTO_LABEL: Record<string, string> = {
  contrato_y_publicado: "🟢 Contrato firmado y anuncio publicado",
  contrato_firmado: "🟢 Contrato firmado",
  anuncio_publicado: "🟢 Anuncio publicado",
  interesado: "🟠 Interesado",
  sin_interes_claro: "🟡 Sin interés claro",
  no_interesado: "🔴 No interesado",
  no_contesta: "⚫ No contesta",
};

export function applyAgenciasFilters<
  T extends {
    nombre: string;
    estado: string;
    seguimiento: string | null;
    provincia_id?: string | null;
    anuncios_count: number;
    origen?: string | null;
    miembros_count?: number | null;
    solicitudes_count?: number | null;
    invitaciones_count?: number | null;
    telefono?: string | null;
    email?: string | null;
    created_at?: string | null;
  },
>(rows: T[], f: AgenciasFilters): T[] {
  const nombre = normalizeText(f.nombre);
  const altaDesde = f.alta_dias != null ? Date.now() - f.alta_dias * 86_400_000 : null;
  return rows.filter((r) => {
    if (f.origen?.length && !(r.origen && f.origen.includes(r.origen))) return false;
    if (f.con_miembros != null && (r.miembros_count ?? 0) > 0 !== f.con_miembros) return false;
    if (f.con_solicitudes != null && (r.solicitudes_count ?? 0) > 0 !== f.con_solicitudes)
      return false;
    if (f.con_invitaciones != null && (r.invitaciones_count ?? 0) > 0 !== f.con_invitaciones)
      return false;
    if (f.con_contacto != null && !!(r.telefono?.trim() || r.email?.trim()) !== f.con_contacto)
      return false;
    if (altaDesde != null && !(r.created_at && new Date(r.created_at).getTime() >= altaDesde))
      return false;
    if (nombre && !normalizeText(r.nombre).includes(nombre)) return false;
    if (f.provincia_ids.length && !(r.provincia_id && f.provincia_ids.includes(r.provincia_id)))
      return false;
    if (f.estado.length && !f.estado.includes(r.estado)) return false;
    if (f.seguimiento.length && !(r.seguimiento && f.seguimiento.includes(r.seguimiento)))
      return false;
    const c = r.anuncios_count ?? 0;
    if (f.anuncios_min != null && c < f.anuncios_min) return false;
    if (f.anuncios_max != null && c > f.anuncios_max) return false;
    return true;
  });
}

export function AgenciasFiltersForm({
  filters,
  onChange,
}: {
  filters: AgenciasFilters;
  onChange: (next: AgenciasFilters) => void;
}) {
  const set = <K extends keyof AgenciasFilters>(k: K, v: AgenciasFilters[K]) =>
    onChange({ ...filters, [k]: v });

  const fnProvincias = useServerFn(listProvincias);
  const provincias = useQuery({
    queryKey: ["provincias"],
    queryFn: () => fnProvincias() as Promise<Array<{ id: string; nombre: string }>>,
  });

  return (
    <Accordion
      type="multiple"
      defaultValue={["nombre", "provincia", "estado", "seguimiento", "anuncios", "actividad"]}
      className="w-full"
    >
      <AccordionItem value="nombre">
        <AccordionTrigger>Nombre</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <Label className="text-xs text-muted-foreground">Nombre de la agencia</Label>
          <Input
            placeholder="Buscar por nombre…"
            value={filters.nombre}
            onChange={(e) => set("nombre", e.target.value)}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="provincia">
        <AccordionTrigger>Provincia</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <Label className="text-xs text-muted-foreground">Provincia</Label>
          <MultiCheck
            values={filters.provincia_ids}
            options={(provincias.data ?? []).map((p) => ({
              value: p.id,
              label: p.nombre,
            }))}
            onChange={(v) => set("provincia_ids", v)}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="estado">
        <AccordionTrigger>Estado interno</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <Label className="text-xs text-muted-foreground">Estado de la agencia</Label>
          <MultiCheck
            values={filters.estado}
            options={AGENCIA_ESTADOS.map((e) => ({
              value: e,
              label: ESTADO_LABEL[e] ?? e,
            }))}
            onChange={(v) => set("estado", v)}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="seguimiento">
        <AccordionTrigger>Estado de seguimiento</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <Label className="text-xs text-muted-foreground">Seguimiento comercial</Label>
          <MultiCheck
            values={filters.seguimiento}
            options={AGENCIA_SEGUIMIENTOS.map((s) => ({
              value: s,
              label: SEGUIMIENTO_LABEL[s] ?? s,
            }))}
            onChange={(v) => set("seguimiento", v)}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="anuncios">
        <AccordionTrigger>Cantidad de anuncios</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Mínimo</Label>
              <NumInput
                value={filters.anuncios_min}
                onChange={(v) => set("anuncios_min", v)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Máximo</Label>
              <NumInput
                value={filters.anuncios_max}
                onChange={(v) => set("anuncios_max", v)}
                placeholder="Sin límite"
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="actividad">
        <AccordionTrigger>Equipo y actividad</AccordionTrigger>
        <AccordionContent className="space-y-3 pb-4">
          <TriState
            label="Miembros"
            value={filters.con_miembros}
            onChange={(v) => set("con_miembros", v)}
            si="Con miembros"
            no="Sin miembros"
          />
          <TriState
            label="Solicitudes"
            value={filters.con_solicitudes}
            onChange={(v) => set("con_solicitudes", v)}
            si="Con solicitudes"
            no="Sin solicitudes"
          />
          <TriState
            label="Invitaciones pendientes"
            value={filters.con_invitaciones}
            onChange={(v) => set("con_invitaciones", v)}
            si="Con invitaciones"
            no="Sin invitaciones"
          />
          <TriState
            label="Datos de contacto"
            value={filters.con_contacto}
            onChange={(v) => set("con_contacto", v)}
            si="Con teléfono o email"
            no="Sin teléfono ni email"
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="origen">
        <AccordionTrigger>Origen</AccordionTrigger>
        <AccordionContent className="space-y-2 pb-4">
          <Label className="text-xs text-muted-foreground">Cómo llegó la agencia</Label>
          <MultiCheck
            values={filters.origen}
            options={AGENCIA_ORIGENES.map((o) => ({
              value: o,
              label: AGENCIA_ORIGEN_LABEL[o] ?? o,
            }))}
            onChange={(v) => set("origen", v)}
          />
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="alta">
        <AccordionTrigger>Fecha de alta</AccordionTrigger>
        <AccordionContent className="pb-4">
          <div className="flex flex-wrap gap-1.5">
            {[{ value: null, label: "Cualquier fecha" }, ...AGENCIA_ALTA_OPTIONS].map((o) => (
              <Chip
                key={String(o.value)}
                active={filters.alta_dias === o.value}
                onClick={() => set("alta_dias", o.value)}
              >
                {o.label}
              </Chip>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "rounded-md border px-2.5 py-1 text-xs transition",
        active ? "border-primary bg-primary/10 text-primary" : "hover:bg-accent",
      )}
    >
      {children}
    </button>
  );
}

/** Selector Todas / Con / Sin para un filtro booleano opcional. */
function TriState({
  label,
  value,
  onChange,
  si,
  no,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
  si: string;
  no: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        <Chip active={value === null} onClick={() => onChange(null)}>
          Indiferente
        </Chip>
        <Chip active={value === true} onClick={() => onChange(true)}>
          {si}
        </Chip>
        <Chip active={value === false} onClick={() => onChange(false)}>
          {no}
        </Chip>
      </div>
    </div>
  );
}
